import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TRENDS, DAYS, DAY_NUMS } from '../../constants/trends';

const W = 320;
const H = 220;
const PAD = 12;

/** Catmull-Rom style smoothing, matching the curved line in the app. */
const smoothPath = (points: [number, number][]) => {
    if (points.length < 2) return '';

    let d = `M ${points[0][0]} ${points[0][1]}`;
    for (let i = 0; i < points.length - 1; i++) {
        const [x0, y0] = points[Math.max(0, i - 1)];
        const [x1, y1] = points[i];
        const [x2, y2] = points[i + 1];
        const [x3, y3] = points[Math.min(points.length - 1, i + 2)];

        const c1x = x1 + (x2 - x0) / 6;
        const c1y = y1 + (y2 - y0) / 6;
        const c2x = x2 - (x3 - x1) / 6;
        const c2y = y2 - (y3 - y1) / 6;

        d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${x2} ${y2}`;
    }
    return d;
};

/** Normalises a series into viewBox coordinates. */
const toPoints = (series: number[]): [number, number][] => {
    const max = Math.max(...series);
    const min = Math.min(...series);
    const range = max - min || 1;
    return series.map((value, i) => [
        (i / (series.length - 1)) * W,
        PAD + (1 - (value - min) / range) * (H - PAD * 2),
    ]);
};

const TrendsChart: React.FC = () => {
    const [activeKey, setActiveKey] = useState(TRENDS[0].key);
    const trend = TRENDS.find((t) => t.key === activeKey) ?? TRENDS[0];

    // `d` is not animatable in most browsers, so tween the points ourselves and
    // rebuild the path each frame.
    const [shown, setShown] = useState(() => toPoints(TRENDS[0].series));
    const fromRef = useRef(shown);
    const rafRef = useRef(0);

    useEffect(() => {
        const target = toPoints(trend.series);
        const from = fromRef.current;
        const start = performance.now();
        const DUR = 520;
        const ease = (t: number) => 1 - Math.pow(1 - t, 3);

        cancelAnimationFrame(rafRef.current);

        const step = (now: number) => {
            const t = Math.min(1, (now - start) / DUR);
            const e = ease(t);
            const next = target.map(([tx, ty], i) => {
                const [fx, fy] = from[i] ?? [tx, ty];
                return [fx + (tx - fx) * e, fy + (ty - fy) * e] as [number, number];
            });
            setShown(next);
            if (t < 1) rafRef.current = requestAnimationFrame(step);
            else fromRef.current = target;
        };

        rafRef.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(rafRef.current);
    }, [trend]);

    const { line, area, points } = useMemo(() => {
        const path = smoothPath(shown);
        return { line: path, area: `${path} L ${W} ${H} L 0 ${H} Z`, points: shown };
    }, [shown]);

    return (
        <div className="grid min-w-0 gap-4">
            {/* metric tabs — underline style, horizontally scrollable */}
            <div className="flex h-7 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {TRENDS.map((t) => {
                    const on = t.key === activeKey;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setActiveKey(t.key)}
                            aria-pressed={on}
                            className="grid h-7 flex-none place-items-center px-6 text-xs tracking-[0.36px] transition-[color,border-color] duration-300"
                            style={{
                                fontWeight: on ? 700 : 500,
                                color: on ? '#FCFFFF' : '#868B91',
                                borderBottom: `2px solid ${on ? '#FCFFFF' : 'transparent'}`,
                            }}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <div className="rounded-[20px] bg-[#202428] px-4 pb-4 pt-6">
              <div className="relative">
                <svg
                    viewBox={`0 0 ${W} ${H}`}
                    preserveAspectRatio="none"
                    className="block h-[200px] w-full"
                    role="img"
                    aria-label={`${trend.label} over the last week`}
                >
                    <defs>
                        <linearGradient id="fkarea" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0" stopColor="#0393E7" stopOpacity="0.3" />
                            <stop offset="0.5" stopColor="#0393E7" stopOpacity="0.1" />
                            <stop offset="1" stopColor="#0393E7" stopOpacity="0" />
                        </linearGradient>
                    </defs>

                    {[4, 53, 102, 151, 200].map((y) => (
                        <line
                            key={y}
                            x1="0"
                            y1={y}
                            x2={W}
                            y2={y}
                            stroke="#868B91"
                            strokeOpacity="0.3"
                            strokeWidth="1"
                        />
                    ))}

                    <path d={area} fill="url(#fkarea)" />
                    <path
                        d={line}
                        fill="none"
                        stroke="#0393E7"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                    />
                </svg>

                {/* Dots are DOM elements, not SVG circles: the chart uses
                    preserveAspectRatio="none", which stretches the viewBox
                    horizontally and would squash circles into ovals. */}
                <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                    {points.map(([x, y], i) => (
                        <span
                            key={i}
                            className="absolute h-2 w-2 rounded-full border-2 border-[#202428] bg-[#0393E7]"
                            style={{
                                left: `${(x / W) * 100}%`,
                                top: `${(y / H) * 100}%`,
                                transform: 'translate(-50%,-50%)',
                            }}
                        />
                    ))}
                </div>
              </div>

                <div className="mt-1 flex">
                    {DAYS.map((d, i) => (
                        <span key={d} className="grid flex-1 justify-items-center">
                            <span className="text-[9px] text-[#868B91]">{d}</span>
                            <span className="text-[8px] text-[rgba(134,139,145,0.7)]">
                                {DAY_NUMS[i]}
                            </span>
                        </span>
                    ))}
                </div>
            </div>

            {/* summary tiles */}
            {/* 22px values like "2,340 kcal" overflow a third of a phone
                screen, so the tiles stack until there is room. */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div className="rounded-2xl bg-[#202428] px-4 py-3.5">
                    <p className="mb-1.5 text-[9px] font-medium uppercase tracking-[0.12em] text-dark-textSubtle">
                        Avg / day
                    </p>
                    <p
                        key={`avg-${trend.key}`}
                        className="animate-value-in text-[22px] font-extrabold leading-none tracking-[-0.02em] text-white"
                    >
                        {trend.avg.toLocaleString()} {trend.unit}
                    </p>
                </div>
                <div className="rounded-2xl bg-[#202428] px-4 py-3.5">
                    <p className="mb-1.5 text-[9px] font-medium uppercase tracking-[0.12em] text-dark-textSubtle">
                        Best day
                    </p>
                    <p
                        key={`max-${trend.key}`}
                        className="animate-value-in text-[22px] font-extrabold leading-none tracking-[-0.02em] text-white"
                    >
                        {Math.max(...trend.series).toLocaleString()} {trend.unit}
                    </p>
                </div>
                <div className="rounded-2xl bg-[#202428] px-4 py-3.5">
                    <p className="mb-1.5 text-[9px] font-medium uppercase tracking-[0.12em] text-dark-textSubtle">
                        Vs last week
                    </p>
                    <p
                        key={`delta-${trend.key}`}
                        className="animate-value-in text-[22px] font-extrabold leading-none tracking-[-0.02em]"
                        style={{ color: trend.delta.startsWith('-') ? '#FF6A59' : '#02F3A2' }}
                    >
                        {trend.delta}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default TrendsChart;
